<template>
  <div v-if="userOriginal">
    <navbar :links="links" />
    <v-layout class="mt-4 mb-2">
      <v-layout column>
        <div
          class="title mb-2 font-weight-bold"
          :class="{
            'warning--text pseudo-anchor--inverted clickable':
              fullUserNameIsEmpty,
          }"
          :data-hj-suppress="!fullUserNameIsEmpty"
          @click="fullUserNameIsEmpty ? (showEditUserNamesDialog = true) : null"
        >
          {{
            fullUserNameIsEmpty
              ? $t('userManagementEditUserNamesEmptyWarning')
              : fullUserName
          }}
        </div>
        <div class="heading grey--text">
          {{ $t('changeAccount') }}
        </div>
      </v-layout>
      <!-- Upload User Avatar Image -->
      <user-avatar-image-upload
        :class="{'mr-2': $vuetify.breakpoint.xsOnly}"
        :user="$user"
        @uploaded="handleUserAvatarUploaded"
      />
    </v-layout>
    <!-- Demo Account & Expiration-->
    <v-layout
      justify-end
      align-baseline
    >
      <div
        v-if="$user && $userprofile.expiration_date"
        class="caption mr-2"
      >
        {{ $t('accountExpiresOn') }}
        <strong>{{
          $userprofile.expiration_date | dayjs('DD.MM.YYYY')
        }}</strong>
      </div>
      <v-chip
        v-if="userOriginal.clientprofile && userOriginal.clientprofile.is_demo"
        small
        :title="$t('isDemoAccount')"
      >
        Demo
      </v-chip>
    </v-layout>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Form fields -->
    <v-layout
      v-if="!loading && userWorkingCopy"
      column
      class="mt-2"
    >
      <v-layout
        align-center
        class="darken-background-on-hover"
      >
        <v-text-field
          :label="$t('username')"
          :value="usernameString"
          prepend-icon="person"
          disabled
          class="v-text-custom-disabled"
        />
        <v-btn
          icon
          class="msr"
          dark
          @click.stop="showEditUserNamesDialog = true"
        >
          <v-icon>edit</v-icon>
        </v-btn>
      </v-layout>
      <v-layout
        align-center
        class="darken-background-on-hover"
      >
        <v-text-field
          :label="$t('email')"
          :value="userWorkingCopy.email"
          prepend-icon="email"
          disabled
          class="v-text-custom-disabled"
        />
        <v-btn
          icon
          class="msr"
          dark
          data-cy="change-email-btn"
          @click.stop="showChangeEmailDialog = true"
        >
          <v-icon>edit</v-icon>
        </v-btn>
      </v-layout>
      <v-layout
        align-center
        class="darken-background-on-hover"
      >
        <v-text-field
          :label="$t('password')"
          value="****************"
          prepend-icon="security"
          disabled
          class="v-text-custom-disabled"
        />
        <v-btn
          icon
          class="msr"
          dark
          data-cy="change-password-btn"
          @click.stop="showChangePasswordDialog = true"
        >
          <v-icon>edit</v-icon>
        </v-btn>
      </v-layout>
      <v-layout
        align-center
        class="darken-background-on-hover"
      >
        <v-text-field
          :label="$t('twoFactorAuthentication')"
          disabled
          :value="twoFactorStatus"
          prepend-icon="lock"
          class="v-text-custom-disabled"
        />
        <v-btn
          icon
          class="msr"
          dark
          data-cy="change-2fa-btn"
          :disabled="$userprofile.is_demo"
          :to="{name: 'twoFactorAuthentication'}"
        >
          <v-icon>edit</v-icon>
        </v-btn>
      </v-layout>
      <!-- Note: type="number" does display a more fitting keyboard on mobile,
		       but sadly it won't trigger a v-model update, so we would not be
		       able to change the phone on mobile. That's why we stick with
		       text for now.
      -->
      <v-flex md6>
        <v-form
          v-model="phoneFormIsValid"
          @submit.prevent
        >
          <v-text-field
            v-model="userWorkingCopy.clientprofile.phone"
            :label="$t('phone')"
            counter="30"
            type="text"
            :rules="[rules.phone]"
            prepend-icon="phone"
            @keyup.native.esc="resetWorkingCopy()"
            @keyup.native.enter="submitUserAccountUpdate()"
          />
        </v-form>
        <!-- Floating buttons for things like the phone -->
        <v-layout
          row
          justify-end
        >
          <btn-abort
            v-if="dirty"
            :disabled="updating"
            @click="resetWorkingCopy()"
          />
          <btn-ok
            v-if="dirty && phoneFormIsValid"
            :loading="updating"
            class="mr-0"
            @click="submitUserAccountUpdate()"
          />
        </v-layout>
        <!-- Language Override -->
        <language-select />
      </v-flex>
    </v-layout>
    <!-- Facebook Logout Button -->
    <v-slide-x-transition>
      <v-layout
        v-if="loggedInFacebookUsername"
        align-center
        :column="$vuetify.breakpoint.xs"
      >
        <v-layout align-center>
          <div style="width: 33px">
            <img
              src="/static/icons8-facebook-96.png"
              width="30"
              height="30"
              style="position: relative; left: -3px; top: 3px"
            >
          </div>
          <div>
            {{ $t('facebookLoggedInAs') }} <b>{{ loggedInFacebookUsername }}</b>
          </div>
        </v-layout>
        <v-btn
          round
          flat
          small
          color="msr"
          @click="facebookLogout()"
        >
          {{ $t('facebookLogoutNow') }}
        </v-btn>
      </v-layout>
    </v-slide-x-transition>
    <!-- Permissions -->
    <permissions
      v-if="!loading && permissionDescriptions.length"
      class="mt-2"
      :permissions="permissionDescriptions"
    />
    <v-divider />
    <!-- Email Subscriptions -->
    <email-subscriptions
      v-if="!loading"
      class="mt-2"
    />
    <v-divider />
    <!-- Delete Account -->
    <v-hover>
      <v-expansion-panel
        slot-scope="{hover}"
        class="mt-2 elevation-0"
      >
        <v-expansion-panel-content>
          <div
            slot="header"
            :class="hover ? 'error--text' : 'default--text'"
          >
            {{ $t('deleteAccountButton') }}
          </div>
          <v-card>
            <v-btn
              large
              block
              round
              color="error"
              class="elevation-0"
              @click="showDeleteAccountDialog = true"
            >
              <v-icon left>
                delete
              </v-icon>
              {{ $t('deleteAccountButton') }}
            </v-btn>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-hover>
    <!-- Dialogs -->
    <edit-user-names-dialog
      v-if="userWorkingCopy"
      :show.sync="showEditUserNamesDialog"
      :user="userWorkingCopy"
      :busy="updating"
      @confirm="
        (newFirstName, newLastName) => renameThisUser(newFirstName, newLastName)
      "
    />
    <change-email-dialog
      v-if="userWorkingCopy"
      ref="emailDialog"
      :show.sync="showChangeEmailDialog"
      :old-email="userWorkingCopy.email"
      @submitted="submitChangeEmailRequest()"
      @aborted="abortEmailChange()"
    />
    <change-password-dialog
      ref="passwordDialog"
      :show.sync="showChangePasswordDialog"
      @submitted="submitUserAccountUpdate()"
      @aborted="abortPasswordChange()"
    />
    <delete-user-account-dialog :show.sync="showDeleteAccountDialog" />
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';

import facebookLogout from '@/mixins/facebookLogout';

import BtnAbort from '@/components/ButtonAbort';
import BtnOk from '@/components/ButtonOk';
import ChangeEmailDialog from '@/components/ChangeEmailDialog';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import DeleteUserAccountDialog from '@/components/DeleteUserAccountDialog';
import EditUserNamesDialog from '@/components/EditUserNamesDialog';
import EmailSubscriptions from '@/components/EmailSubscriptions';
import LanguageSelect from '@/components/LanguageSelect';
import Navbar from '@/components/Navbar';
import Permissions from '@/components/Permissions';
import UserAvatarImageUpload from '@/components/UserAvatarImageUpload';

export default {
  components: {
    BtnAbort,
    BtnOk,
    ChangeEmailDialog,
    ChangePasswordDialog,
    DeleteUserAccountDialog,
    EditUserNamesDialog,
    EmailSubscriptions,
    LanguageSelect,
    Navbar,
    Permissions,
    UserAvatarImageUpload,
  },
  mixins: [facebookLogout],
  data() {
    return {
      loading: false,
      updating: false,
      links: ['dashboard', 'account'],
      userOriginal: null,
      userWorkingCopy: null,
      phoneFormIsValid: true,
      rules: {
        phone: (value) => {
          const pattern = /^[0-9\+\/ \-]*$/;
          return pattern.test(value) || this.$t('invalidPhone');
        },
      },
      showEditUserNamesDialog: false,
      showChangeEmailDialog: false,
      showChangePasswordDialog: false,
      showDeleteAccountDialog: false,
      avatarKey: 1,
    };
  },
  computed: {
    /**
     * Analyze User/Profile and return a list of items to display.
     *
     * Returns:
     *   Array[Object] like: [
     *     {
     *       icon: '...',
     *       text: '...',
     *       highlight: null,
     *     },
     *   ]
     */
    permissionDescriptions() {
      let items = [];
      if (!this.userOriginal) {
        return items;
      }

      if (this.$store.getters.userManagesContentForAtLeastOneGroup) {
        const uploadgroups = this.$store.getters.uploadgroups;
        const groupNames = uploadgroups.map((group) => group.name).join(', ');
        const item = {
          icon: 'file_upload',
          text: this.$t('uploadGroupsPermission') + ': ',
          highlight: groupNames,
        };
        items.push(item);
      }

      if (
        this.$store.getters.isUsermanagerOrManagedCoadmin &&
        !!this.$userprofile.managed_workspace
      ) {
        const managedWorkspace = this.$userprofile.managed_workspace;
        const item = {
          icon: 'supervisor_account',
          text: this.$t('userManagementPermission') + ': ',
          highlight: managedWorkspace.name,
        };
        items.push(item);
      }

      if (this.$store.getters.userIsSomeAssociationGroupContentManager) {
        const item = {
          icon: 'bar_chart',
          text: this.$t('associationGroupContentManagerPermission'),
        };
        items.push(item);
      }

      return items;
    },
    fullUserName() {
      if (!this.userOriginal) {
        return '';
      }
      let label =
        (this.userOriginal.first_name || '') +
        ' ' +
        (this.userOriginal.last_name || '');
      return label;
    },
    fullUserNameIsEmpty() {
      return this.fullUserName.trim() === '';
    },
    usernameString() {
      return (
        this.userWorkingCopy.first_name + ' ' + this.userWorkingCopy.last_name
      );
    },
    twoFactorStatus() {
      let twofactorauth = this.$userprofile.twofactorauth_enabled;
      let method_string = null;
      if (twofactorauth) {
        if (twofactorauth == 'sms') {
          method_string = this.$t('twoFactorAuthenticationSMSTitle');
        }
        if (twofactorauth == 'otp') {
          method_string = this.$t(
            'twoFactorAuthenticationAuthenticatorAppTitle'
          );
        }
        return (
          this.$t('twoFactorAuthenticationEnabled') + ' (' + method_string + ')'
        );
      } else {
        return this.$t('twoFactorAuthenticationDisabled');
      }
    },
    dirty() {
      const dirty =
        JSON.stringify(this.userOriginal) !==
        JSON.stringify(this.userWorkingCopy);
      return dirty;
    },
  },
  mounted() {
    this.fetchAndInitialize();
  },
  methods: {
    fetchAndInitialize() {
      this.loading = true;
      this.$store.dispatch('RETRIEVE_CURRENT_USER').then(() => {
        this.copyCurrentUserForEditing();
        this.loading = false;
      });
    },
    copyCurrentUserForEditing() {
      this.userOriginal = deepCopyObject(this.$user);
      // Fix dirty-check for fields that can be null initially but
      // empty strings after touching them.
      this.userOriginal.clientprofile.phone =
        this.userOriginal.clientprofile.phone || '';

      this.resetWorkingCopy();
    },
    /**
     * Refetch user to display avatar image.
     */
    handleUserAvatarUploaded(response) {
      this.fetchAndInitialize();
    },
    submitChangeEmailRequest() {
      let vm = this;
      let payload = {};

      let dialog = this.$refs.emailDialog;
      if (!dialog.emailFormIsValid) {
        return;
      }

      payload.new_email = dialog.newEmail;
      this.$api
        .requestEmailChange(payload)
        .then((response) => {
          vm.$bus.$emit('alert-info', {
            message: 'changeEmailSuccessInfo',
            translate: true,
            timeout: 0,
          });
          vm.abortEmailChange();
        })
        .catch((error) => {
          const alreadyInUse =
            error.response.data.indexOf('MultipleObjectsReturned') !== -1;
          if (alreadyInUse) {
            vm.$bus.$emit('alert-warning', {
              message: 'changeEmailAlreadyInUse',
              translate: true,
              timeout: 8000,
            });
            vm.$refs.emailDialog.reset();
          } else {
            vm.$bus.$emit('alert-error', {
              message: 'changeEmailFailedInfo',
              translate: true,
              timeout: 0,
            });
            vm.abortEmailChange();
          }
        });
    },
    submitUserAccountUpdate() {
      if (this.dirty && !this.phoneFormIsValid) {
        return;
      }

      let vm = this;
      let payload = {};

      if (
        this.userWorkingCopy.clientprofile.phone !==
        this.userOriginal.clientprofile.phone
      ) {
        payload.phone = this.userWorkingCopy.clientprofile.phone;
      }

      let passwordChangeRequested = false;
      let dialog = this.$refs.passwordDialog;
      if (dialog.canSubmitPasswordChange) {
        payload.old_password = dialog.passwords.oldPassword;
        payload.new_password = dialog.passwords.newPassword1;
        passwordChangeRequested = true;
      }

      this.updating = true;
      this.$api
        .updateUserAccount(payload)
        .then((response) => {
          if (passwordChangeRequested) {
            this.abortPasswordChange();
            // The password has been changed, meaning the existing
            // Token has been rotated. We'll want the user to login
            // again.
            this.$store.commit('UNSET_CURRENT_USER');
            this.$router.push({
              path: '/login',
              query: {
                email: this.userOriginal.email,
              },
            });

            vm.$bus.$emit('alert-info', {
              message: 'loginAfterPasswordChange',
              translate: true,
              timeout: 20000,
            });

            return;
          }

          let user = response.data;
          this.$store.commit('SET_CURRENT_USER', user);
          this.userOriginal = user;
          this.resetWorkingCopy();

          vm.$bus.$emit('alert-success', {
            message: 'accountUpdatedSuccess',
            translate: true,
          });
          this.updating = false;
        })
        .catch((error) => {
          this.resetWorkingCopy();
          this.abortPasswordChange();

          vm.$bus.$emit('alert-error', {
            message: 'accountUpdatedError',
            translate: true,
          });
          this.updating = false;
        });
    },
    renameThisUser(newFirstName, newLastName) {
      this.updating = true;
      this.$api
        .editUserNames(this.$user.id, newFirstName, newLastName)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'accountUpdatedSuccess',
            translate: true,
          });
          this.fetchAndInitialize();
          this.showEditUserNamesDialog = false;
        })
        .finally(() => {
          this.updating = false;
        });
    },
    abortEmailChange() {
      this.showChangeEmailDialog = false;
      if (this.$refs.emailDialog) {
        this.$refs.emailDialog.reset();
      }
    },
    abortPasswordChange() {
      this.showChangePasswordDialog = false;
      if (this.$refs.passwordDialog) {
        this.$refs.passwordDialog.reset();
      }
    },
    resetWorkingCopy() {
      this.userWorkingCopy = deepCopyObject(this.userOriginal);
    },
  },
};
</script>

<style scoped>
.prepend-icon {
  font-size: 24px;
  min-width: 40px;
  display: flex;
  justify-content: flex-start;
}

.edit-btn {
  margin-left: 0;
}

.darken-background-on-hover:hover {
  background: rgba(0, 0, 0, 0.04);
}
</style>



// WEBPACK FOOTER //
// src/pages/UserAccount.vue